import { makeObservable, observable, action, reaction } from 'mobx';
import {
  initialRailState,
  toggleLeft as slotToggleLeft,
  toggleRight as slotToggleRight,
  claimLeft,
  claimRight,
  peekLeft,
  routeChange,
  contentClick as slotContentClick,
  type RailState,
} from '@shared/common/layout/rail-slot.util';
import type { Rail } from '@/layouts/navigation';
import { adoptLegacyPreference, readPreference, writePreference } from '@/lib/preferences';
import type { RootStore } from './RootStore';

/** Before 2026-11-01 pins were stored once per BROWSER; the first account to sign in adopts them (lib/preferences). */
const LEGACY_STORAGE_KEY = 'agentstudio.nav.v1';
const PREF = 'nav.v2';

interface PersistedState {
  pinned: string[];
  collapsed: string[];
}

// Manages the user's navigation: which destination keys are pinned, which rail
// sections are collapsed (both persisted per ACCOUNT and environment, and dropped
// on sign-out or a change of account), and which rail owns the single wide slot.
// Pins are preferences, never grants: the rail shows only pins the resolved
// navigation (StudioModuleStore) still offers this person.
//
// The wide slot is driven by the SAME state machine the provider web app uses
// (_Shared.Lib/src/common/layout/rail-slot.util.ts) — imported, never copied —
// so the two apps expand and collapse identically. It is deliberately NOT
// persisted: like provider web, the shell boots left-wide and the route decides.

export class NavStore {
  @observable pinned: Set<string> = new Set();
  @observable collapsed: Set<string> = new Set();
  @observable.ref rail: RailState = initialRailState;

  /** The rail that owned the last route we saw — slot hand-overs happen on CHANGES of owner. */
  private routeRail: Rail | null = null;

  /** Whose preferences these are; null while signed out. */
  private subject: string | number | null = null;

  constructor(root: RootStore) {
    makeObservable(this);

    reaction(
      () => root.authStore.user?.userId ?? null,
      (userId) => this.load(userId),
      { fireImmediately: true }
    );

    reaction(
      () => ({ pinned: [...this.pinned], collapsed: [...this.collapsed] }),
      (state) => writePreference(PREF, this.subject, state)
    );
  }

  @action private load(userId: string | number | null): void {
    this.subject = userId;
    const saved = readPreference<PersistedState>(PREF, userId) ?? adoptLegacyPreference<PersistedState>(LEGACY_STORAGE_KEY, PREF, userId);
    this.pinned = new Set(saved?.pinned ?? []);
    this.collapsed = new Set(saved?.collapsed ?? []);
  }

  isPinned(key: string): boolean {
    return this.pinned.has(key);
  }

  isCollapsed(sectionId: string): boolean {
    return this.collapsed.has(sectionId);
  }

  /** Is this rail showing labels (200px) right now? */
  isWide(side: Rail): boolean {
    return this.rail.wideSlot === side;
  }

  @action togglePin(key: string): void {
    if (this.pinned.has(key)) this.pinned.delete(key);
    else this.pinned.add(key);
    // Force MobX to see the change.
    this.pinned = new Set(this.pinned);
  }

  @action toggleSection(sectionId: string): void {
    if (this.collapsed.has(sectionId)) this.collapsed.delete(sectionId);
    else this.collapsed.add(sectionId);
    this.collapsed = new Set(this.collapsed);
  }

  @action clearPins(): void {
    this.pinned = new Set();
  }

  // ── Wide slot ─────────────────────────────────────────────────────────────

  /** A rail's chevron: wide → icons (the other widens); icons → wide. */
  @action toggleRail(side: Rail): void {
    this.rail = side === 'left' ? slotToggleLeft(this.rail) : slotToggleRight(this.rail);
  }

  /**
   * A click inside a 60px rail that asks to see labels. On a right-rail page a
   * left claim is only a peek: a click back in the page returns the slot.
   */
  @action claimRail(side: Rail): void {
    if (side === 'right') this.rail = claimRight(this.rail);
    else this.rail = this.routeRail === 'right' ? peekLeft(this.rail) : claimLeft(this.rail);
  }

  /** Route changed: hand the slot over only when the OWNING rail changes (rail-slot.util owns the rule). */
  @action routeChanged(owner: Rail): void {
    this.rail = routeChange(this.rail, this.routeRail, owner);
    this.routeRail = owner;
  }

  /** A click in the page content settles an outstanding peek. */
  @action contentClicked(): void {
    if (this.rail.leftPeek) this.rail = slotContentClick(this.rail, false);
  }
}
